import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useCMSPageField } from '@/cms/fields'
import { useSendOptimizelyEvent } from '@/scopes/common/infrastructure/ab-testing/optimizely/useSendOptimizelyEvent'
import { getBrandName } from '@/scopes/common/infrastructure/analytics/brandNameMapper'
import ga4analyticsClient from '@/scopes/common/infrastructure/analytics/ga4analyticsClient'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useCommonDependency } from '@/scopes/common/infrastructure/dependencies/common'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { createBrowserQueryStringKeepingParams } from '@/scopes/common/infrastructure/query-strings/browserQueryStringKeepingParams'
import { useAccommodationContext } from '@/scopes/prices/components/AccommodationContext'
import { pricesActions } from '@/scopes/prices/domain/actions/pricesActions'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { createAvailabilityApiClient } from '@/scopes/prices/infrastructure/availability/availabilityApiClient'
import {
  createFilterQueryStringSerializer,
} from '@/scopes/prices/infrastructure/filter-query-string-serializer/filterQueryStringSerializer'
import { createFiltersApiClient } from '@/scopes/prices/infrastructure/filters/filtersAPIClient'
import { createFiltersResponseMapper } from '@/scopes/prices/infrastructure/filters/filtersResponseMapper'
import { createPricesApiClient } from '@/scopes/prices/infrastructure/pricesByDuration/pricesAPIClient'
import { createPromotedPriceApiClient } from '@/scopes/prices/infrastructure/promoted-price/promotedPriceApiClient'
import { createPricesQueryParametersToStateMapper } from '@/scopes/prices/infrastructure/queryParametersToStateMapper/pricesQueryParametersToStateMapper'
import { createRoomSelectorApiClient } from '@/scopes/prices/infrastructure/rooms/roomSelectorApiClient'
import { newPricesStore } from '@/scopes/prices/infrastructure/store'
import { createBusSelectorApiClient } from '@/scopes/prices/infrastructure/transport/bus/busSelectorApiClient'
import { createFlightSelectorApiClient } from '@/scopes/prices/infrastructure/transport/flight/flightSelectorApiClient'
import { createSelfDriveSelectorApiClient } from '@/scopes/prices/infrastructure/transport/selfdrive/selfDriveSelectorApiClient'
import { pricesPassThroughParams, PricesPassThroughParams } from '@/scopes/prices/types'
import { getDefaultSharedFilterSelection } from '@/scopes/search/domain/policies/defaultFilters'

interface PricesActionsContextProviderProps {
  children: ReactNode
}

export function PricesActionsContextProvider({ children }: PricesActionsContextProviderProps) {
  const [initialized, setInitialized] = useState(false)
  const { contextItemId, accoId, bookingGateId, promotedPriceId } = useAccommodationContext()
  const brandName = useConfiguration('siteName')
  const accoBrand = getBrandName(brandName)
  const queryStringClient = useCommonDependency('queryStringClient')
  const analytics = useCMSPageField('PDP.pricetable.analytics')
  const refreshPromotedPrice = useFeatureFlag('BAHAMAS_ENABLE_PROMOTED_PRICE_REFRESH')
  const publicationCode = useConfiguration('publicationCode')

  const browserQuery = createBrowserQueryStringKeepingParams<PricesPassThroughParams>({
    passThroughParams: pricesPassThroughParams,
  })

  const { sendEvent: sendOptimizelyEvent } = useSendOptimizelyEvent()
  const defaultSelection = getDefaultSharedFilterSelection(publicationCode)
  const {
    initPrices,
    initPriceTable,
    selectFilterMonth,
    selectFilter,
    selectRange,
    selectPrice,
    selectRoom,
    setOutboundRouteId,
    setInboundRouteId,
    setOutboundBusId,
    setInboundBusId,
    resetPrices,
  } = useMemo(() => {
    return pricesActions({
      filtersApiClient: createFiltersApiClient({
        filtersResponseMapper: createFiltersResponseMapper(),
        browserQuery,
      }),
      pricesApiClient: createPricesApiClient({ browserQuery }),
      roomSelectorApiClient: createRoomSelectorApiClient({ browserQuery }),
      flightSelectorApiClient: createFlightSelectorApiClient({ browserQuery }),
      selfDriveSelectorApiClient: createSelfDriveSelectorApiClient({ browserQuery }),
      busSelectorApiClient: createBusSelectorApiClient({ browserQuery }),
      analyticsClient: ga4analyticsClient,
      store: newPricesStore,
      queryStringSerializer: createFilterQueryStringSerializer({ defaultSelection }),
      queryParametersToStateMapper: createPricesQueryParametersToStateMapper(),
      browserQueryString: browserQuery,
      promotedPriceAPIClient: createPromotedPriceApiClient({ browserQuery }),
      isAccoAvailableApiClient: createAvailabilityApiClient({}),
      optimizelySendEvent: sendOptimizelyEvent,
    })
  }, [browserQuery, defaultSelection])

  const initialize = useCallback(async () => {
    const originList = queryStringClient.getValue('originList')
    await initPrices({
      accoId,
      bookingGateId,
      contextItemId,
      analytics: analytics ? { ...analytics, originList: originList || undefined, accoBrand } : null,
      initialFilterSelection: defaultSelection,
      promotedPriceId,
      refreshPromotedPrice,
    })
  }, [
    accoId,
    bookingGateId,
    contextItemId,
    initPrices,
    queryStringClient,
    analytics,
    accoBrand,
    publicationCode,
    promotedPriceId,
    refreshPromotedPrice,
    defaultSelection,
  ])

  useEffect(() => {
    if (!initialized) {
      initialize()
      setInitialized(true)
    }
  }, [initialize, initialized])

  const value = useMemo(
    () => ({
      initPrices,
      initPriceTable,
      selectFilterMonth,
      selectFilter,
      selectRange,
      selectPrice,
      selectRoom,
      setOutboundRouteId,
      setInboundRouteId,
      setOutboundBusId,
      setInboundBusId,
      resetPrices,
    }),
    [
      initPrices,
      initPriceTable,
      selectFilterMonth,
      selectFilter,
      selectRange,
      selectPrice,
      selectRoom,
      setOutboundRouteId,
      setInboundRouteId,
      setOutboundBusId,
      setInboundBusId,
      resetPrices,
    ],
  )

  return (
    <PricesActionsContext
      value={value}
    >
      {children}
    </PricesActionsContext>
  )
}
